import { formatMoney } from "./quotationUtils";

/**
 * e.g. template https://shop.com/t/{{1}} + example https://shop.com/t/abc → "abc"
 */
export function extractUrlButtonDynamicPart(urlTemplate, fullExample) {
  const t = (urlTemplate || "").trim();
  const ex = (fullExample || "").trim();
  if (!t || !ex) return ex || "";
  const marker = "{{1}}";
  const pos = t.indexOf(marker);
  if (pos === -1) return ex;
  const prefix = t.slice(0, pos);
  const suffix = t.slice(pos + marker.length);
  if (ex.startsWith(prefix) && ex.endsWith(suffix)) {
    return ex.slice(prefix.length, ex.length - suffix.length);
  }
  return ex;
}

/** Button index keys for website CTAs that use {{1}} in the URL. */
export function getDynamicWebsiteButtonSlots(template, variableSamples = {}) {
  const stored = variableSamples?.buttons || {};
  const ctaButtons = template?.attrs?.ctaButtons || [];
  const slots = {};

  ctaButtons.forEach((btn, idx) => {
    const type = String(btn?.type || "").toLowerCase();
    const url = typeof btn?.value === "string" ? btn.value : "";
    if (type !== "website" || !/\{\{1\}\}/.test(url)) return;

    const key = String(idx);
    const storedVal = stored[key] ?? stored[idx];
    slots[key] = storedVal != null
      ? String(storedVal)
      : extractUrlButtonDynamicPart(url, btn.urlExample || "");
  });

  return slots;
}

export function buildButtonVariableSamplesFromCta(ctaButtons = []) {
  const buttons = {};
  ctaButtons.forEach((btn, idx) => {
    const type = String(btn?.type || "").toLowerCase();
    const url = typeof btn?.value === "string" ? btn.value.trim() : "";
    if (type !== "website" || !/\{\{1\}\}/.test(url)) return;
    buttons[String(idx)] = extractUrlButtonDynamicPart(url, btn.urlExample || "");
  });
  return buttons;
}

/** Empty user-fillable values for each template variable slot. */
export function emptyTemplateVariablesFromSamples(samples) {
  const fill = (slot) => Object.fromEntries(Object.keys(slot || {}).map((k) => [k, ""]));
  return {
    header: fill(samples?.header),
    body: fill(samples?.body),
    footer: fill(samples?.footer),
    buttons: fill(samples?.buttons),
  };
}

/** Extract {{1}}, {{2}}, … placeholders from template text. */
export function extractNumericTemplateVariables(text) {
  const matches = [...String(text || "").matchAll(/\{\{(\d+)\}\}/g)];
  const keys = [...new Set(matches.map((m) => m[1]))].sort((a, b) => Number(a) - Number(b));
  return Object.fromEntries(keys.map((k) => [k, ""]));
}

function mergeVariableSlots(storedSlot, text) {
  const fromText = extractNumericTemplateVariables(text);
  const keys = new Set([
    ...Object.keys(storedSlot || {}),
    ...Object.keys(fromText),
  ]);
  if (keys.size === 0) return {};
  return Object.fromEntries(
    [...keys].sort((a, b) => Number(a) - Number(b)).map((k) => [k, storedSlot?.[k] ?? ""]),
  );
}

/** Variable slots required by a template (stored samples + body/header/footer text). */
export function inferTemplateVariableSlots(template) {
  const stored = getEffectiveTemplateVariableSamples(template);
  const bodyText = template?.attrs?.body || template?.getBody?.() || "";
  const headerText = template?.attrs?.headerText || template?.getHeader?.() || "";
  const footerText = template?.attrs?.footer || template?.getFooter?.() || "";
  return {
    header: mergeVariableSlots(stored.header, headerText),
    body: mergeVariableSlots(stored.body, bodyText),
    footer: mergeVariableSlots(stored.footer, footerText),
    buttons: stored.buttons || {},
  };
}

/** Prefill template variables from quotation fields (name, number, total). */
export function buildQuotationVariableDefaults(quotation, variableSlots) {
  const customerName = quotation?.contact_name || quotation?.lead_title || "Customer";
  const label = quotation?.quotation_label
    || (quotation?.quotation_number ? `QUOT-${quotation.quotation_number}` : "Quotation");
  const total = formatMoney(quotation?.total_minor, quotation?.currency || "INR");
  const canonical = [customerName, label, total];

  const fillSection = (slot) => {
    const keys = Object.keys(slot || {}).sort((a, b) => Number(a) - Number(b));
    if (keys.length === 0) return {};
    return Object.fromEntries(
      keys.map((key, idx) => [key, canonical[idx] ?? canonical[0] ?? ""]),
    );
  };

  return {
    header: fillSection(variableSlots?.header),
    body: fillSection(variableSlots?.body),
    footer: fillSection(variableSlots?.footer),
    buttons: Object.fromEntries(
      Object.keys(variableSlots?.buttons || {}).map((k) => [
        k,
        variableSlots.buttons[k] || "",
      ]),
    ),
  };
}

/** Merge stored variable_sample with dynamic website button slots from ctaButtons. */
export function getEffectiveTemplateVariableSamples(template) {
  let variableSamples = { header: {}, body: {}, footer: {}, buttons: {} };
  const raw = template?.attrs?.variableSamples;
  if (raw) {
    try {
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      variableSamples = { ...variableSamples, ...parsed };
    } catch (e) {
      /* ignore */
    }
  }
  return {
    header: variableSamples.header || {},
    body: variableSamples.body || {},
    footer: variableSamples.footer || {},
    buttons: getDynamicWebsiteButtonSlots(template, variableSamples),
  };
}
